/**
 * Trade Level Calculator Service
 * PHASE 7.2: Calculates Entry, SL, TP levels for trade setups
 *
 * Calculates optimal trade entry, stop loss, and take profit levels
 * based on pivots, Fibonacci, EMAs, and swing levels.
 */

/**
 * Calculate trade levels based on direction and available data
 *
 * @param {Object} params - Trade calculation parameters
 * @param {number} params.currentPrice - Current market price
 * @param {string} params.direction - 'LONG' or 'SHORT'
 * @param {Object} params.pivots - Pivot levels { PP, R1, R2, R3, S1, S2, S3 }
 * @param {Array} params.fibLevels - Fibonacci levels array
 * @param {Object} params.emaLevels - EMA values { ema60, ema100 }
 * @param {number} params.swingHigh - Recent swing high price
 * @param {number} params.swingLow - Recent swing low price
 * @param {number} params.atr - Average True Range for buffer calculation
 * @returns {Object} Trade levels with reasons
 */
export const calculateTradeLevels = ({
    currentPrice,
    direction,
    pivots = {},
    fibLevels = [],
    emaLevels = {},
    swingHigh,
    swingLow,
    atr = 0
}) => {
    if (!currentPrice || !direction) {
        return null;
    }

    const isLong = direction === 'LONG';
    const buffer = atr > 0 ? atr * 0.2 : currentPrice * 0.001; // 20% of ATR or 0.1% of price

    let stopLoss, tp1, tp2, slReason, tp1Reason, tp2Reason;

    // Build level arrays from available data
    const buildLevelArray = (filterFn, sortFn) => {
        const levels = [];

        // Add pivot levels
        if (pivots.S1 && filterFn(pivots.S1)) levels.push({ level: pivots.S1, source: 'Pivot S1' });
        if (pivots.S2 && filterFn(pivots.S2)) levels.push({ level: pivots.S2, source: 'Pivot S2' });
        if (pivots.S3 && filterFn(pivots.S3)) levels.push({ level: pivots.S3, source: 'Pivot S3' });
        if (pivots.R1 && filterFn(pivots.R1)) levels.push({ level: pivots.R1, source: 'Pivot R1' });
        if (pivots.R2 && filterFn(pivots.R2)) levels.push({ level: pivots.R2, source: 'Pivot R2' });
        if (pivots.R3 && filterFn(pivots.R3)) levels.push({ level: pivots.R3, source: 'Pivot R3' });
        if (pivots.PP && filterFn(pivots.PP)) levels.push({ level: pivots.PP, source: 'Pivot PP' });

        // Add Fibonacci levels
        if (Array.isArray(fibLevels)) {
            fibLevels.forEach(fib => {
                if (fib.price && filterFn(fib.price)) {
                    levels.push({ level: fib.price, source: `Fib ${fib.level}` });
                }
            });
        }

        // Add EMA levels
        if (emaLevels.ema60 && filterFn(emaLevels.ema60)) levels.push({ level: emaLevels.ema60, source: 'EMA 60' });
        if (emaLevels.ema100 && filterFn(emaLevels.ema100)) levels.push({ level: emaLevels.ema100, source: 'EMA 100' });

        // Add swing levels
        if (swingHigh && filterFn(swingHigh)) levels.push({ level: swingHigh, source: 'Swing High' });
        if (swingLow && filterFn(swingLow)) levels.push({ level: swingLow, source: 'Swing Low' });

        return levels.sort(sortFn);
    };

    if (isLong) {
        // LONG: SL below support, TP above current
        const supports = buildLevelArray(
            level => level < currentPrice,
            (a, b) => b.level - a.level // Nearest first (descending)
        );

        const resistances = buildLevelArray(
            level => level > currentPrice,
            (a, b) => a.level - b.level // Nearest first (ascending)
        );

        if (supports.length > 0) {
            stopLoss = supports[0].level - buffer;
            slReason = supports[0].source;
        } else {
            // Fallback: Use percentage-based SL
            stopLoss = currentPrice * 0.995; // 0.5% below
            slReason = 'Default (0.5%)';
        }

        if (resistances.length >= 2) {
            tp1 = resistances[0].level;
            tp1Reason = resistances[0].source;
            tp2 = resistances[1].level;
            tp2Reason = resistances[1].source;
        } else if (resistances.length === 1) {
            tp1 = resistances[0].level;
            tp1Reason = resistances[0].source;
            tp2 = currentPrice * 1.015; // 1.5% above
            tp2Reason = 'Extended (1.5%)';
        } else {
            // Fallback: Use percentage-based TP
            tp1 = currentPrice * 1.008; // 0.8% above
            tp1Reason = 'Default (0.8%)';
            tp2 = currentPrice * 1.015; // 1.5% above
            tp2Reason = 'Extended (1.5%)';
        }
    } else {
        // SHORT: SL above resistance, TP below current
        const resistances = buildLevelArray(
            level => level > currentPrice,
            (a, b) => a.level - b.level // Nearest first (ascending)
        );

        const supports = buildLevelArray(
            level => level < currentPrice,
            (a, b) => b.level - a.level // Nearest first (descending)
        );

        if (resistances.length > 0) {
            stopLoss = resistances[0].level + buffer;
            slReason = resistances[0].source;
        } else {
            // Fallback: Use percentage-based SL
            stopLoss = currentPrice * 1.005; // 0.5% above
            slReason = 'Default (0.5%)';
        }

        if (supports.length >= 2) {
            tp1 = supports[0].level;
            tp1Reason = supports[0].source;
            tp2 = supports[1].level;
            tp2Reason = supports[1].source;
        } else if (supports.length === 1) {
            tp1 = supports[0].level;
            tp1Reason = supports[0].source;
            tp2 = currentPrice * 0.985; // 1.5% below
            tp2Reason = 'Extended (1.5%)';
        } else {
            // Fallback: Use percentage-based TP
            tp1 = currentPrice * 0.992; // 0.8% below
            tp1Reason = 'Default (0.8%)';
            tp2 = currentPrice * 0.985; // 1.5% below
            tp2Reason = 'Extended (1.5%)';
        }
    }

    return {
        entry: currentPrice,
        stopLoss,
        takeProfit1: tp1,
        takeProfit2: tp2,
        reasons: {
            sl: slReason,
            tp1: tp1Reason,
            tp2: tp2Reason
        }
    };
};

/**
 * Calculate Risk:Reward ratio
 *
 * @param {number} entry - Entry price
 * @param {number} stopLoss - Stop loss price
 * @param {number} takeProfit - Take profit price
 * @returns {Object} R:R ratio and quality assessment
 */
export const calculateRiskReward = (entry, stopLoss, takeProfit) => {
    if (!entry || !stopLoss || !takeProfit) {
        return { ratio: null, isGood: false, label: 'N/A' };
    }

    const risk = Math.abs(entry - stopLoss);
    const reward = Math.abs(takeProfit - entry);

    if (risk === 0) {
        return { ratio: null, isGood: false, label: 'N/A' };
    }

    const ratio = reward / risk;
    const isGood = ratio >= 1.5;
    const isAcceptable = ratio >= 1.0;

    let label;
    if (ratio >= 2.0) label = 'Excellent';
    else if (ratio >= 1.5) label = 'Good';
    else if (ratio >= 1.0) label = 'Acceptable';
    else label = 'Poor';

    return {
        ratio: ratio.toFixed(2),
        isGood,
        isAcceptable,
        label
    };
};

/**
 * Calculate pip distance between two prices
 *
 * @param {number} targetPrice - Target price level
 * @param {number} currentPrice - Current price
 * @returns {string} Formatted pip distance with +/- sign
 */
export const calculatePipDistance = (targetPrice, currentPrice) => {
    if (!targetPrice || !currentPrice) return '--';

    // Determine pip value based on price magnitude
    const pipValue = currentPrice > 100 ? 0.01 : 0.0001;
    const pips = Math.round((targetPrice - currentPrice) / pipValue);

    return pips > 0 ? `+${pips}p` : `${pips}p`;
};

/**
 * Determine trade direction from signal verdict
 *
 * @param {Object} verdict - Signal verdict { type, action, score }
 * @returns {string} 'LONG', 'SHORT', or null
 */
export const getTradeDirection = (verdict) => {
    if (!verdict) return null;

    const type = verdict.type?.toLowerCase();
    const action = verdict.action?.toUpperCase();

    if (type === 'buy' || action?.includes('BUY') || action === 'BULLISH') {
        return 'LONG';
    }
    if (type === 'sell' || action?.includes('SELL') || action === 'BEARISH') {
        return 'SHORT';
    }

    return null;
};

/**
 * Format price for display
 *
 * @param {number} price - Price value
 * @param {number} referencePrice - Reference price for decimal determination
 * @returns {string} Formatted price string
 */
export const formatTradePrice = (price, referencePrice) => {
    if (!price && price !== 0) return '--';

    const ref = referencePrice || price;
    if (ref > 1000) return price.toFixed(2);      // Indices like XAU, US30
    if (ref > 10) return price.toFixed(4);         // Pairs like USDJPY
    return price.toFixed(5);                        // Standard forex pairs
};

// Default export for convenience
const tradeLevelCalculator = {
    calculateTradeLevels,
    calculateRiskReward,
    calculatePipDistance,
    getTradeDirection,
    formatTradePrice
};

export default tradeLevelCalculator;
